/**
 * REST API bodies (docs/design/05 §2, §8; 07 §2, §7). Every endpoint takes `Authorization: Bearer
 * <Supabase JWT>` (verified against the JWKS by the Worker, 01 §10); the user id comes from `sub`,
 * never from a body. Request schemas are strict; responses are what the Worker returns.
 *
 * | Endpoint | Request | Response |
 * |---|---|---|
 * | `POST /api/queue` | {@link QueueJoinRequestSchema} | {@link QueueStatusSchema} |
 * | `GET /api/queue` | — | {@link QueueStatusSchema} |
 * | `DELETE /api/queue` | — | {@link QueueStatusSchema} (`idle`) |
 * | `POST /api/rooms` | {@link CreateRoomRequestSchema} | {@link RoomViewSchema} |
 * | `GET /api/rooms/:code` | — | {@link RoomViewSchema} |
 * | `POST /api/rooms/:code/join` | {@link RoomDeckRequestSchema} | {@link RoomViewSchema} |
 * | `POST /api/rooms/:code/deck` | {@link RoomDeckRequestSchema} | {@link RoomViewSchema} |
 * | `POST /api/rooms/:code/start` | — | {@link MatchCreatedSchema} |
 * | `POST /api/rooms/:code/leave` | — | {@link RoomViewSchema} |
 * | `POST /api/bot-matches` | {@link BotMatchRequestSchema} | {@link MatchCreatedSchema} |
 * | `GET /api/bot-decks` | — | {@link BotPresetSchema}[] |
 * | `POST /api/match/:id/ticket` | — | {@link TicketResponseSchema} |
 * | `GET /api/matches` | — | {@link MatchListItemSchema}[] (the caller's recent matches) |
 * | `GET /api/matches/:id` | — | {@link MatchSummarySchema} |
 * | `GET /api/replays/:id[?share=<token>]` | — | {@link ReplayFileSchema} (participants, or a participant's share token, Q38) |
 * | `GET /api/replays/s/:token` | — | {@link ReplayFileSchema} (anyone with a `play.replay_share` token, Q38) |
 * | `POST /api/invites` | {@link CreateInviteRequestSchema} | {@link InviteSchema} |
 * | `POST /api/invites/:id/answer` | {@link InviteAnswerRequestSchema} | {@link InviteSchema} |
 * | `POST /api/beacon` | {@link BeaconSchema} | 204 |
 * | `GET /healthz` | — | {@link HealthSchema} |
 *
 * Errors are {@link ApiErrorSchema} with an HTTP status.
 */
import { z } from 'zod';
import { CommandSchema } from './commands';
import { MatchHeaderSchema, SeatInfoSchema } from './messages';
import { CardIdSchema, PlayerIdSchema, PrintingIdSchema, TierSchema, int, text } from './primitives';
import { GameEndReasonSchema, GameResultSchema, ModeConfigSchema, SeatSettingsSchema } from './view';

/** A Runebound deck id (`public.deck.id`, a uuid v7 minted on the device). */
export const DeckIdSchema = text(64);
/** Room codes are short and shareable: `play.runebound.gg/r/<code>`. */
export const RoomCodeSchema = z.string().regex(/^[A-Z0-9]{4,10}$/);
export const MatchIdSchema = text(128);

export const ApiErrorSchema = z.strictObject({
  error: z.strictObject({
    code: z.enum([
      'unauthorized',
      'forbidden',
      'notFound',
      'invalidRequest',
      'deckInvalid',
      'tierDisabled',
      'blocked',
      'conflict',
      'rateLimited',
      'server',
    ]),
    message: z.string().max(500),
    /** Deck problems when `code` is `deckInvalid` (catalog `DeckIssue`s). */
    issues: z
      .array(
        z.strictObject({
          code: text(40),
          message: z.string().max(300),
          rule: z.string().max(80),
          cardId: z.string().max(128).nullable(),
        }),
      )
      .exactOptional(),
  }),
});

// ── Casual queue (05 §8: FIFO per tier, no skill-based matching) ─────────────────────────────────
export const QueueJoinRequestSchema = z.strictObject({ deckId: DeckIdSchema, tier: TierSchema });

export const QueueStatusSchema = z.discriminatedUnion('status', [
  z.strictObject({ status: z.literal('idle') }),
  z.strictObject({ status: z.literal('queued'), queue: text(64), tier: TierSchema, since: z.iso.datetime() }),
  z.strictObject({ status: z.literal('matched'), matchId: MatchIdSchema }),
]);

// ── Private rooms (05 §8: the host picks the tier; the invitee sees it before joining) ───────────
export const CreateRoomRequestSchema = z.strictObject({
  tier: TierSchema,
  deckId: DeckIdSchema.exactOptional(),
});

export const RoomDeckRequestSchema = z.strictObject({ deckId: DeckIdSchema });

export const RoomViewSchema = z.strictObject({
  code: RoomCodeSchema,
  url: z.url(),
  tier: TierSchema,
  mode: z.literal('duel'),
  format: z.literal('standard'),
  status: z.enum(['open', 'ready', 'started', 'closed']),
  host: SeatInfoSchema.omit({ seat: true }),
  guest: SeatInfoSchema.omit({ seat: true }).nullable(),
  /** Whether each side has picked a (valid) deck. Deck contents stay private. */
  hostReady: z.boolean(),
  guestReady: z.boolean(),
  matchId: MatchIdSchema.nullable(),
  createdAt: z.iso.datetime(),
});

export const MatchCreatedSchema = z.strictObject({ matchId: MatchIdSchema });

// ── Practice bot (owner decision Q50: the bot plays tiers 1-2 through the rules knowledge layer) ──
export const BotMatchRequestSchema = z.strictObject({
  deckId: DeckIdSchema,
  tier: z.union([z.literal(1), z.literal(2)]),
  /** The bot's deck: a copy of yours, or one of the server's preset decks. */
  opponent: z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('mirror') }),
    z.strictObject({ kind: z.literal('preset'), presetId: text(64) }),
  ]),
  /** The practice bot's difficulty (default `normal`: greedy one-ply evaluation; `easy`: beginner play). */
  level: z.enum(['easy', 'normal']).optional(),
  /** How fast the bot acts (the player's "Practice bot speed" setting; default `normal`). Pacing only. */
  speed: z.enum(['slow', 'normal', 'fast', 'instant']).optional(),
});

/** A server preset deck the bot can play (`GET /api/bot-decks`; deck contents stay server-side). */
export const BotPresetSchema = z.strictObject({
  id: text(64),
  name: z.string().max(120),
  legendCardId: CardIdSchema,
  championCardId: CardIdSchema.nullable(),
});

// ── WebSocket tickets (05 §2: browsers can't set headers on WebSocket requests) ──────────────────
export const TicketResponseSchema = z.strictObject({
  ticket: z.string().regex(/^[A-Za-z0-9_-]{16,128}$/),
  expiresInMs: int(0, 600_000),
  /** Path to open: `/match/<id>/ws?ticket=…`. */
  url: z.string().max(400),
  role: z.enum(['player', 'spectator']),
});

// ── Friend invites (05 §8, 07 §3: mutual follows, blocks respected; Realtime-delivered rows) ─────
export const CreateInviteRequestSchema = z.strictObject({
  to: z.uuid(),
  roomCode: RoomCodeSchema,
});

export const InviteAnswerRequestSchema = z.strictObject({ accept: z.boolean() });

/** A `play.invite` row as its sender and recipient see it. */
export const InviteSchema = z.strictObject({
  id: z.uuid(),
  from: z.uuid(),
  to: z.uuid(),
  roomCode: RoomCodeSchema,
  tier: TierSchema,
  status: z.enum(['pending', 'accepted', 'declined', 'expired', 'cancelled']),
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
});

// ── One-tap hand-off from runebound.gg and the app (owner decision Q11; 07 §2, research 05 §1.4 B) ─
/** The `play-handoff` Edge Function's answer (runebound-app): a single-use magic-link token hash. */
export const HandoffResponseSchema = z.strictObject({
  tokenHash: z.string().min(16).max(256),
  expiresAt: z.iso.datetime(),
});

/**
 * The fragment of `https://play.runebound.gg/auth/handoff#th=<token_hash>&deck=<deck id>&next=<path>`.
 * A fragment keeps the token out of server logs and the Referer header.
 */
export const HandoffFragmentSchema = z.strictObject({
  th: z.string().min(16).max(256),
  deck: DeckIdSchema.exactOptional(),
  /** A same-site path to continue to (validated: must start with `/`, no `//`). */
  next: z
    .string()
    .max(200)
    .regex(/^\/(?!\/)[^\s]*$/)
    .exactOptional(),
});

/** The "Play this deck" link Runebound's web and app open (07 §7). */
export function playDeckUrl(deckId: string, origin = 'https://play.runebound.gg'): string {
  return `${origin}/play?deck=${encodeURIComponent(deckId)}`;
}

/** Parses a hand-off URL fragment (`#th=…&deck=…`); `null` if it isn't a valid hand-off. */
export function parseHandoffFragment(fragment: string): z.infer<typeof HandoffFragmentSchema> | null {
  const raw: Record<string, string> = {};
  for (const pair of (fragment.startsWith('#') ? fragment.slice(1) : fragment).split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    const key = eq < 0 ? pair : pair.slice(0, eq);
    const value = eq < 0 ? '' : pair.slice(eq + 1);
    try {
      raw[decodeURIComponent(key)] = decodeURIComponent(value.replace(/\+/g, ' '));
    } catch {
      return null; // malformed percent-encoding
    }
  }
  const parsed = HandoffFragmentSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

// ── Decks (07 §4: own decks only, Q16) ──────────────────────────────────────────────────────────
/** A `play.my_decks()` row for the deck picker. */
export const DeckSummarySchema = z.strictObject({
  id: DeckIdSchema,
  name: z.string().max(200),
  format: z.string().max(32).nullable(),
  legendPrintingId: PrintingIdSchema.nullable(),
  updatedAt: z.iso.datetime({ offset: true }).nullable(),
});

// ── Match summaries and replays (05 §10; Q38: replays private to players, shareable by a participant) ─
export const MatchSummarySchema = z.strictObject({
  header: MatchHeaderSchema,
  status: z.enum(['live', 'ended', 'aborted']),
  result: GameResultSchema.nullable(),
  endReason: GameEndReasonSchema.nullable(),
  endedAt: z.iso.datetime().nullable(),
  replayId: text(128).nullable(),
});

/** One of the caller's matches as `GET /api/matches` lists it (from `play.match` + `play.match_player`). */
export const MatchListItemSchema = z.strictObject({
  matchId: MatchIdSchema,
  kind: z.enum(['queue', 'room', 'bot']),
  tier: TierSchema,
  status: z.enum(['live', 'ended', 'aborted']),
  createdAt: z.iso.datetime({ offset: true }),
  endedAt: z.iso.datetime({ offset: true }).nullable(),
  /** `play.match.end_reason` (`disconnect` is the engine's `abandon`; `aborted` covers no-result games). */
  endReason: z.enum(['points', 'concede', 'timeout', 'disconnect', 'draw', 'aborted']).nullable(),
  turns: int(0, 100_000).nullable(),
  seats: z.array(
    z.strictObject({
      seat: PlayerIdSchema,
      userId: z.uuid().nullable(),
      bot: z.boolean(),
      result: z.enum(['win', 'loss', 'draw', 'abandon']).nullable(),
      points: int(0, 1000).nullable(),
      legendCardId: CardIdSchema.nullable(),
      championCardId: CardIdSchema.nullable(),
    }),
  ),
  replayId: text(128).nullable(),
});

export const DeckCardSchema = z.strictObject({ cardId: CardIdSchema, printingId: PrintingIdSchema.nullable() });
export const DeckEntrySchema = z.strictObject({ ...DeckCardSchema.shape, quantity: int(1, 99) });
export const DeckListSchema = z.strictObject({
  legend: DeckCardSchema,
  champion: DeckCardSchema.nullable(),
  main: z.array(DeckEntrySchema),
  runes: z.array(DeckEntrySchema),
  battlefields: z.array(DeckEntrySchema),
  sideboard: z.array(DeckEntrySchema),
});

/** `Partial<SeatSettings>` with exact optional fields (no explicit `undefined`). */
const PartialSeatSettingsSchema = z.strictObject(
  Object.fromEntries(Object.entries(SeatSettingsSchema.shape).map(([k, v]) => [k, v.exactOptional()])) as {
    [K in keyof typeof SeatSettingsSchema.shape]: z.ZodExactOptional<(typeof SeatSettingsSchema.shape)[K]>;
  },
);

export const GameSetupSchema = z.strictObject({
  matchId: MatchIdSchema,
  tier: TierSchema,
  mode: ModeConfigSchema,
  seats: z.array(
    z.strictObject({ id: PlayerIdSchema, deck: DeckListSchema, settings: PartialSeatSettingsSchema.exactOptional() }),
  ),
  seed: z.string().regex(/^[0-9a-f]{32}$/),
  catalogVersion: text(64),
  flags: z.record(z.string(), z.union([z.boolean(), z.number(), z.string()])).exactOptional(),
});

export const HostCommandSchema = z.discriminatedUnion('t', [
  z.strictObject({
    t: z.literal('endGame'),
    reason: z.enum(['timeout', 'abandon', 'noResult', 'draw', 'aborted']),
    loser: PlayerIdSchema.exactOptional(),
  }),
  z.strictObject({ t: z.literal('setTier'), tier: TierSchema }),
]);

export const RecordedCommandSchema = z.union([
  z.strictObject({ by: PlayerIdSchema, cmd: CommandSchema }),
  z.strictObject({ by: z.literal('host'), cmd: HostCommandSchema }),
]);

/** The archived replay (`R2 replays/<matchId>.json`): everything needed to re-run the match. */
export const ReplayFileSchema = z.strictObject({
  header: MatchHeaderSchema,
  record: z.strictObject({
    setup: GameSetupSchema,
    commands: z.array(RecordedCommandSchema),
    checkpoints: z
      .array(z.strictObject({ seq: int(0, Number.MAX_SAFE_INTEGER), hash: z.string().regex(/^[0-9a-f]{64}$/) }))
      .exactOptional(),
  }),
});

// ── Operations ──────────────────────────────────────────────────────────────────────────────────
export const HealthSchema = z.strictObject({
  ok: z.literal(true),
  /** Git SHA of the deployed build (release verification polls for it, 01 §8). */
  sha: z.string().max(64),
  protocol: int(1, 9999),
  engine: text(64),
  catalog: text(64),
});

/** Client crash beacon (01 §9). */
export const BeaconSchema = z.strictObject({
  sha: z.string().max(64),
  matchId: MatchIdSchema.nullable(),
  seat: PlayerIdSchema.nullable(),
  seq: int(0, Number.MAX_SAFE_INTEGER).nullable(),
  message: z.string().max(2000),
  stack: z.string().max(8000).nullable(),
  userAgent: z.string().max(400),
});
